class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.cartOverlay = this.querySelector(".m-cart__overlay");
    this.rootUrl = window.Shopify.routes.root;

    this.setHeaderCartIconAccessibility();
    this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this));
    this.addEventListener("click", (event) => {
      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) {
        this.close();
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add("m-cart-drawer--active");
    requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5");
      this.cartDrawerInner.style.setProperty("--translate-x", "0");
    });
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
    document.documentElement.classList.add("prevent-scroll");
  }

  close() {
    this.style.setProperty("--m-bg-opacity", "0");
    this.cartDrawerInner.style.setProperty("--translate-x", "100%");
    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      document.documentElement.classList.remove("prevent-scroll");
    }, 300);
  }

  renderContents(parsedState) {
    this.classList.contains("m-cart--empty") && this.classList.remove("m-cart--empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    setTimeout(() => {
      this.open();
    });
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  onCartDrawerUpdate(updateFooter = true) {
    fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer&_=${Date.now()}`, { cache: "no-store" })
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          if (section.block === "cart-items") {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
          } else {
            if (updateFooter) {
              const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
            }
          }
        });
      })
      .catch((e) => {
        console.error(e);
      });
    this.getCart().then((cart) => {
      this.classList.toggle("m-cart--empty", cart.item_count === 0);
      this.updateCartCount(cart.item_count);
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("m-cart-drawer", MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
    ];
  }
}

customElements.define("m-cart-drawer-items", MCartDrawerItems);

/* ------------------------------------------ */
/* Gift Config                                */
/* ------------------------------------------ */

const GIFT_VARIANTS = [
  45319319879724,
  45319333904428
];

// Tier base choices (distinct IDs only)
const VARIANTS_BY_TOTAL = {
  1000: [45319319879724],                  // Tier1: 1 distinct gift
  3000: [45319319879724, 45319333904428]   // Tier2: 2 distinct gifts
};

const TIER1_CENTS = getGiftTierThresholds().tier1;
const TIER2_CENTS = getGiftTierThresholds().tier2;

/* ------------------------------------------ */
/* Drawer progress bar + free gift rows       */
/* ------------------------------------------ */

class MGiftProgress extends HTMLElement {
  connectedCallback() {
    const total = parseInt(this.dataset.cartTotal, 10) || 0;
    const { shipping, tier1, tier2 } = getGiftTierThresholds();
    const money = (cents) => formatMoney(cents, window.MinimogSettings.money_format);
    const { studPrice, chainPrice, giftTotal } = this.dataset;

    let progress, heading, message;
    if (total >= tier2) {
      progress = 100;
      heading = "You've Unlocked Free Shipping + 2 Free Gifts 🎉";
      message = `You've unlocked all 2 Gifts worth ${giftTotal}! + Free Shipping 🎁`;
    } else if (total >= tier1) {
      progress = 66.66 + ((total - tier1) / (tier2 - tier1)) * 33.34;
      heading = "You've Unlocked Free Shipping + 1 Free Gifts 🎉";
      message = `Add ${money(tier2 - total)} to unlock <b>Free Chain worth ${chainPrice}</b> ⛓️`;
    } else if (total >= shipping) {
      progress = 33.33 + ((total - shipping) / (tier1 - shipping)) * 33.33;
      heading = "You've Unlocked Free Shipping 🎉";
      message = `Add ${money(tier1 - total)} to unlock <b>Free Earrings worth ${studPrice}</b> 🎁`;
    } else {
      progress = (total / shipping) * 33.33;
      heading = "Unlock Free Shipping + Gifts 🎁";
      message = `Add ${money(shipping - total)} to unlock <b>Free Shipping</b> 🚚`;
    }

    this.querySelector("[data-gift-heading]").textContent = heading;
    this.querySelector("[data-gift-message]").innerHTML = message;
    this.querySelector("[data-gift-fill]").style.width = `${progress}%`;

    const reached = { shipping: total >= shipping, tier1: total >= tier1, tier2: total >= tier2 };
    this.querySelectorAll("[data-gift-milestone]").forEach((milestone) => {
      milestone.classList.toggle("active", reached[milestone.dataset.giftMilestone]);
    });

    // FREE gift rows rendered by the drawer snippet
    const root = this.closest("[data-minimog-cart-items]") || document;
    const tier = total >= tier2 ? 2 : total >= tier1 ? 1 : 0;
    const giftRows = root.querySelector("[data-gift-rows]");
    if (giftRows) giftRows.style.display = tier > 0 ? "" : "none";
    root.querySelectorAll("[data-gift-tier]").forEach((row) => {
      row.style.display = tier >= parseInt(row.dataset.giftTier, 10) ? "" : "none";
    });
  }
}
customElements.define("m-gift-progress", MGiftProgress);

function distinctGiftsForTier(subtotalCents) {
  if (subtotalCents >= TIER2_CENTS) {
    const set = Array.from(new Set(VARIANTS_BY_TOTAL[3000] || []));
    return set.slice(0, 2);
  }
  if (subtotalCents >= TIER1_CENTS) {
    const set = Array.from(new Set(VARIANTS_BY_TOTAL[1000] || []));
    return set.slice(0, 1);
  }
  return []; // tier0
}


/* ------------------------------------------ */
/* Cart Tier Status (tier0 aware)             */
/* ------------------------------------------ */

function getCartTierStatus(cartTotalPrice) {
  const subtotalCents = cartTotalPrice;

  if (subtotalCents >= TIER2_CENTS) {
    return { tier: 'tier2', minAmount: TIER2_CENTS / 100, gifts: distinctGiftsForTier(subtotalCents) };
  } else if (subtotalCents >= TIER1_CENTS) {
    return {
      tier: 'tier1',
      minAmount: TIER1_CENTS / 100,
      maxAmount: (TIER2_CENTS - 1) / 100,
      gifts: distinctGiftsForTier(subtotalCents)
    };
  } else {
    return {
      tier: 'tier0',
      nextTierAmount: TIER1_CENTS / 100,
      neededAmount: (TIER1_CENTS - subtotalCents) / 100,
      gifts: [] // tier0 => cleanup
    };
  }
}

// Track current tier for external reads
let currentCartTier = 'tier0';


/* ------------------------------------------ */
/* Helpers                                    */
/* ------------------------------------------ */

async function fetchCart() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Failed to fetch cart');
    const cart = await res.json();
    const tierStatus = getCartTierStatus(cart.total_price);
    currentCartTier = tierStatus.tier;
    console.log(`🔄 Cart Tier: ${currentCartTier} | Total: ${(cart.total_price / 100).toFixed(2)} ${cart.currency}`);
    return cart;
  } catch (error) {
    console.error('Error fetching cart:', error);
    return null;
  }
}

function isFreeGiftLine(line) {
  if (!GIFT_VARIANTS.includes(line.variant_id)) return false;
  return (
    line.final_line_price === 0 ||
    (line.discounted_price === 0 && line.quantity > 0) ||
    (line.line_price === 0 && line.original_line_price === 0)
  );
}
function isGiftVariantLine(line) { return GIFT_VARIANTS.includes(line.variant_id); }

function toCountsDistinctOneEach(arr) {
  const m = {};
  for (const id of Array.from(new Set(arr))) m[id] = 1; // qty=1 per gift
  return m;
}

function collectFreeGiftLines(cart) {
  return cart.items
    .map((line, idx) => ({ id: line.id, key: line.key, variant_id: line.variant_id, quantity: line.quantity, index: idx, _raw: line }))
    .filter(l => isFreeGiftLine(l._raw));
}
function collectAnyGiftLines(cart) {
  return cart.items
    .map((line, idx) => ({ id: line.id, key: line.key, variant_id: line.variant_id, quantity: line.quantity, index: idx, _raw: line }))
    .filter(l => isGiftVariantLine(l._raw));
}

function pickVariantToAddAvoidingDuplicates(cart, preferredId) {
  const presentVariants = new Set(collectAnyGiftLines(cart).map(l => l.variant_id));
  if (!presentVariants.has(preferredId)) return preferredId;
  for (const id of GIFT_VARIANTS) { if (!presentVariants.has(id)) return id; }
  return preferredId;
}


/* ------------------------------------------ */
/* Validation & Guards (existence only)       */
/* ------------------------------------------ */

let DISABLE_UPSELL = false;               // disables adds/trim for tier1/2, not tier0 cleanup
let ENSURE_RUNNING = false;               // 🔒 re-entrancy lock
const ADD_DEBOUNCE_MS = 2500;             // ⏱️ per-variant add debounce
const _recentAdds = new Map();            // variantId -> timestamp (ms)
const _variantCheckCache = new Map();

function _isValidVariantId(id) { return Number.isInteger(id) && id > 0; }

/** /variants/:id.js -> { exists } */
async function checkVariantExists(variantId) {
  if (_variantCheckCache.has(variantId)) return _variantCheckCache.get(variantId);
  if (!_isValidVariantId(variantId)) {
    const out = { exists: false };
    _variantCheckCache.set(variantId, out);
    return out;
  }
  try {
    const res = await fetch(`/variants/${variantId}.js`, { method: 'GET' });
    const ok = res.ok;
    const out = { exists: ok };
    if (ok) await res.json(); // warm cache; price is irrelevant
    _variantCheckCache.set(variantId, out);
    return out;
  } catch {
    const out = { exists: false };
    _variantCheckCache.set(variantId, out);
    return out;
  }
}


/* ------------------------------------------ */
/* Cart API Operations                        */
/* ------------------------------------------ */

async function addVariantsInBatch(items) {
  if (!items || !items.length) return null;
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) throw new Error('Failed to add items');
    return await res.json();
  } catch (error) {
    console.error('Error adding items:', error);
    return null;
  }
}

async function updateCartItems(updates) {
  if (!updates.length) return null;
  try {
    const updatesData = { updates: {} };
    updates.forEach(u => { updatesData.updates[u.id] = u.quantity; });
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(updatesData)
    });
    if (!res.ok) throw new Error('Failed to update cart');
    return await res.json();
  } catch (error) {
    console.error('Error updating cart:', error);
    return null;
  }
}


/* ------------------------------------------ */
/* Tier0 cleanup: remove ALL gift lines       */
/* ------------------------------------------ */

async function removeAllGiftVariantLines(cart) {
  const giftLines = cart.items
    .map((line) => ({ id: line.id, variant_id: line.variant_id, quantity: line.quantity }))
    .filter(l => GIFT_VARIANTS.includes(l.variant_id));
  if (!giftLines.length) return;
  const updates = giftLines.map(l => ({ id: l.id, quantity: 0 }));
  await updateCartItems(updates);
}


/* ------------------------------------------ */
/* Main Gift Enforcement Logic                */
/* ------------------------------------------ */

async function ensureGiftsForTiers() {
  if (ENSURE_RUNNING) return;
  ENSURE_RUNNING = true;

  try {
    let cart = await fetchCart();
    if (!cart) return;

    const tierStatus = getCartTierStatus(cart.total_price);

    /* ---- Tier0: ALWAYS clean up gift lines (free or paid) ---- */
    if (tierStatus.tier === 'tier0') {
      await removeAllGiftVariantLines(cart);
      return; // UI refresh in finally
    }

    if (DISABLE_UPSELL) return;

    // Validate existence only (discount engine will make them free)
    let neededGiftIds = tierStatus.gifts || [];
    if (!neededGiftIds.length) return;

    // ***** UPDATED BEHAVIOR: skip only invalid IDs instead of disabling *****
    const existsChecks = await Promise.all(neededGiftIds.map(id => checkVariantExists(id)));
    const validIds = neededGiftIds.filter((id, i) => existsChecks[i] && existsChecks[i].exists);

    // If none valid, do nothing this run (keep upsell enabled for future runs)
    if (!validIds.length) return;

    currentCartTier = tierStatus.tier;

    // Desired: distinct gifts, qty=1 each
    const desiredCounts = toCountsDistinctOneEach(validIds);

    // A) Normalize ANY gift lines to desired set (qty=1) and remove undesired
    const anyGiftLinesForTrim = collectAnyGiftLines(cart); // free or paid
    const updates = [];
    const seen = new Set();

    for (const line of anyGiftLinesForTrim) {
      const vid = line.variant_id;
      const want = desiredCounts[vid] || 0; // 1 if desired this tier, else 0

      if (want) {
        // Keep only one line for each desired gift, qty=1
        if (seen.has(vid)) {
          // duplicate desired gift -> remove
          updates.push({ id: line.id, quantity: 0 });
        } else {
          seen.add(vid);
          if (line.quantity !== 1) updates.push({ id: line.id, quantity: 1 });
        }
      } else {
        // Not desired at this tier -> remove (free or paid)
        updates.push({ id: line.id, quantity: 0 });
      }
    }
    if (updates.length) {
      await updateCartItems(updates);
      cart = await fetchCart();
      if (!cart) return;
    }

    // B) Add any missing gifts (avoid duplicates; count ANY gift lines present)
    const anyGiftLines = collectAnyGiftLines(cart);
    const presentSet = new Set(anyGiftLines.map(l => l.variant_id));

    const toAdd = [];
    for (const vid of Object.keys(desiredCounts).map(Number)) {
      if (presentSet.has(vid)) continue; // already have it (free or paid)
      const now = Date.now();
      const last = _recentAdds.get(vid) || 0;
      if (now - last < ADD_DEBOUNCE_MS) continue; // debounce
      const chosen = pickVariantToAddAvoidingDuplicates(cart, vid);
      if (!toAdd.includes(chosen)) toAdd.push(chosen);
      _recentAdds.set(vid, now);
    }

    if (toAdd.length) {
      const itemsToAdd = toAdd.map(id => ({ id, quantity: 1 }));
      await addVariantsInBatch(itemsToAdd);
    }

  } catch (e) {
    console.warn('ensureGiftsForTiers soft error:', e);
  } finally {
    updateCartDrawer();
    ENSURE_RUNNING = false;
  }
}


/* ------------------------------------------ */
/* Event Wiring                               */
/* ------------------------------------------ */

document.addEventListener('DOMContentLoaded', () => {
  setTimeout(ensureGiftsForTiers, 500);
});
document.addEventListener('cart:request-completed', ensureGiftsForTiers);

// AJAX interception
(function patchAjaxCartMutations() {
  const origFetch = window.fetch;
  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : input.url;
    const isCartMutation = url && (
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/clear')
    );
    const result = await origFetch.apply(this, arguments);
    if (isCartMutation) {
      if (!ENSURE_RUNNING) setTimeout(ensureGiftsForTiers, 120);
    }
    return result;
  };
})();

/* Theme drawer refresh hook */
function updateCartDrawer() {
  const cartDrawer = document.querySelector('m-cart-drawer');
  if (cartDrawer && typeof cartDrawer.onCartDrawerUpdate === 'function') {
    cartDrawer.onCartDrawerUpdate(true);
  }
}


/* ------------------------------------------ */
/* Export for External Use                    */
/* ------------------------------------------ */

window.cartTier = {
  getCurrentTier: () => currentCartTier,          // 'tier0' | 'tier1' | 'tier2'
  recompute: ensureGiftsForTiers,
  disableUpsell: () => { DISABLE_UPSELL = true; },
  enableUpsell: () => { DISABLE_UPSELL = false; }
};

// Optional CommonJS export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    ensureGiftsForTiers,
    fetchCart,
    getCartTierStatus,
    currentCartTier
  };
}